import { Button, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import Card from 'components/card/Card'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { isNil, sumBy, toInteger } from 'lodash'
import HydrobioAction from 'quality/actions/HydrobioAction'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import useBoolean from 'utils/customHook/useBoolean'
import useSandreList from 'utils/customHook/useSandreList'
import useSandreListIndexed from 'utils/customHook/useSandreListIndexed'
import { useShallowEqualSelector } from 'utils/customHook/useShallowEqualSelector'
import PropTypes from 'prop-types'
import Input from 'components/forms/Input'
import Textarea from 'components/forms/Textarea'
import ConfirmModal from 'components/modal/ConfirmModal'
import { none } from 'ol/centerconstraint'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { STATION_TYPE } from '../../../../station/constants/StationConstants'

const isHugeWaterFlow = qualitometer => toInteger(qualitometer?.stationType) === STATION_TYPE.GRAND_COURS_D_EAU

const SampleDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    selectedSample,
}) => {
    const {
        hydrobioOperation,
        qualitometer,
    } = useSelector(store => ({
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
        qualitometer: store.QualityReducer.qualitometer,
    }), shallowEqual)

    const sandreFacies = useSandreList('PRELEVEMENTS.FACIES')
    const sandreDiatoms = useSandreList('PRELEVEMENTS.DIATOMEES')
    const sandreSealingType = useSandreList('PRELEVEMENTS.TYPECOLMATAGE')
    const sandreHeight = useSandreList('PRELEVEMENTS.HAUTEUR')
    const sandreMaterial = useSandreList('PRELEVEMENTS.MATERIELS')
    const sandreShade = useSandreList('PRELEVEMENTS.OMBRAGE')
    const sandreSealing = useSandreList('PRELEVEMENTS.COLMATAGE')
    const sandreSpeed = useSandreList('PRELEVEMENTS.VITESSE')
    const sandrePhase = useSandreList('PRELEVEMENTS.PHASE')
    const sandreSubstrat = useSandreList('SUBSTRAT')
    const sandreStability = useSandreList('PRELEVEMENTS.STABILITE')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [sample, setSample] = useState({})

    useEffect(() => {
        if (!isOpen) return
        setSample(selectedSample ?? {})
    }, [isOpen, selectedSample])

    const formattedSandreHeight = useMemo(() => {
        return sandreHeight.map(h => ({
            ...h,
            name: `${h.name} (${h.comment})`,
        }))
    }, [sandreHeight])

    const enlien = (h) => {
        switch (hydrobioOperation.support) {
            case 13:
                if (h.mnemonique.startsWith('S') || h.mnemonique === '0') {
                    return true
                }
                break
            case 10:
                if (h.mnemonique !== 'D' && h.mnemonique.startsWith('D') || h.mnemonique === '0') {
                    return true
                }
                break
            case 27:
                if (h.mnemonique.startsWith('M') || h.mnemonique === '0') {
                    return true
                }
                break
            case 29:
                if (h.mnemonique.startsWith('O') || h.mnemonique === '0') {
                    return true
                }
                break
            case 16:
                if (h.mnemonique.startsWith('Z') || h.mnemonique === '0') {
                    return true
                }
                break
            case 11:
                if (h.mnemonique.startsWith('Z') || h.mnemonique === '0') {
                    return true
                }
                break
            case none:
                return true
            default :
                if (!h.mnemonique.startsWith('S') ||!h.mnemonique.startsWith('D') ||!h.mnemonique.startsWith('M') ||!h.mnemonique.startsWith('Z') ||!h.mnemonique.startsWith('O') || h.mnemonique === '0') {
                    return true
                }
                break
        }
        return false
    }
    const sandreSub = sandreSubstrat.filter(enlien)

    const formattedSandreSubstrat = useMemo(() => {
        return sandreSub.map(h => ({
            ...h,
            name: `${h.name} ${showSandreCode ? `[${h.mnemonique}]` : ''}`,
        }))
    }, [sandreSub, showSandreCode])

    const formattedSandreMaterial = useMemo(() => {
        return sandreMaterial.map(h => ({
            ...h,
            name: `${h.name} ${showSandreCode ? `[${h.mnemonique}]` : ''}`,
        }))
    }, [sandreMaterial, showSandreCode])

    return (
        <Dialog
            maxWidth='lg'
            onClose={onClose}
            open={isOpen}
        >
            <DefaultDialogTitle
                title={i18n.sample}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container spacing={1} alignItems='center'>
                            {hydrobioOperation.support === 13 && (
                                <>
                                    <Grid size={2}>
                                        <Input
                                            title={i18n.sample}
                                            maxLength={10}
                                            value={sample.sampleCode}
                                            onChange={v => setSample(p => ({ ...p, sampleCode: v }))}
                                        />
                                    </Grid>
                                    <Grid size={2}>
                                        <Select
                                            label={i18n.phase}
                                            options={sandrePhase}
                                            value={sample.phase}
                                            onChange={v => setSample(p => ({ ...p, phase: v }))}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid size={4}>
                                <Select
                                    label={i18n.substrate}
                                    options={formattedSandreSubstrat}
                                    value={sample.substrateCode}
                                    onChange={v => setSample(p => ({ ...p, substrateCode: v }))}
                                    keyLabel='name'
                                    keyValue='mnemonique'
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    label={i18n.material}
                                    options={formattedSandreMaterial}
                                    value={sample.material}
                                    onChange={v => setSample(p => ({ ...p, material: v }))}
                                    keyLabel='name'
                                    keyValue='mnemonique'
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    label={i18n.speedClass}
                                    options={sandreSpeed}
                                    value={sample.speedClass}
                                    onChange={v => setSample(p => ({ ...p, speedClass: v }))}
                                />
                            </Grid>
                            <Grid size={4}>
                                {isHugeWaterFlow(qualitometer) ? (
                                    <Select
                                        label={i18n.heightWithUnit}
                                        options={formattedSandreHeight}
                                        value={sample.waterHeight}
                                        onChange={v => setSample(p => ({ ...p, waterHeight: v }))}
                                    />
                                ) : (
                                    <NumberField
                                        title={i18n.heightWithUnit}
                                        value={sample.waterHeight}
                                        onChange={v => setSample(p => ({ ...p, waterHeight: v }))}
                                        floatValue
                                    />
                                )}
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    label={i18n.secondSubstrate}
                                    options={formattedSandreSubstrat}
                                    value={sample.secondSubstrateCode}
                                    onChange={v => setSample(p => ({ ...p, secondSubstrateCode: v }))}
                                    keyLabel='name'
                                    keyValue='mnemonique'
                                />
                            </Grid>
                            {hydrobioOperation.support === 10 && (
                                <Grid size={4}>
                                    <NumberField
                                        title={i18n.nbSupports}
                                        value={sample.nbSupports}
                                        onChange={v => setSample(p => ({ ...p, nbSupports: v }))}
                                    />
                                </Grid>
                            )}
                            <Grid size={4}>
                                <NumberField
                                    title={`${i18n.distanceShore} (m)`}
                                    value={sample.distanceShore}
                                    onChange={v => setSample(p => ({ ...p, distanceShore: v }))}
                                    floatValue
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    label={i18n.sealingType}
                                    options={sandreSealingType}
                                    value={sample.sealingType}
                                    onChange={v => setSample(p => ({ ...p, sealingType: v }))}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    label={i18n.sealingIntensity}
                                    options={sandreSealing}
                                    value={sample.sealing}
                                    onChange={v => setSample(p => ({ ...p, sealing: v }))}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    title={i18n.vegetation}
                                    value={sample.vegetation}
                                    onChange={v => setSample(p => ({ ...p, vegetation: v }))}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Select
                                    label={i18n.shade}
                                    options={sandreShade}
                                    value={sample.shade}
                                    onChange={v => setSample(p => ({ ...p, shade: v }))}
                                />
                            </Grid>
                            {hydrobioOperation.support === 13 && (
                                <Grid size={4}>
                                    <Select
                                        label={i18n.stability}
                                        options={sandreStability}
                                        value={sample.stability}
                                        onChange={v => setSample(p => ({ ...p, stability: v }))}
                                    />
                                </Grid>
                            )}
                            {hydrobioOperation.support === 10 && (
                                <>
                                    <Grid size={4}>
                                        <Select
                                            label={i18n.morphologicalFacies}
                                            options={sandreFacies}
                                            value={sample.secondaryFacies}
                                            onChange={v => setSample(p => ({ ...p, secondaryFacies: v }))}
                                        />
                                    </Grid>
                                    <Grid size={4}>
                                        <Select
                                            label={i18n.diatoms}
                                            options={sandreDiatoms}
                                            value={sample.diatoms}
                                            onChange={v => setSample(p => ({ ...p, diatoms: v }))}
                                        />
                                    </Grid>
                                </>
                            )}
                            <Grid size={12}>
                                <Textarea
                                    title={i18n.comment}
                                    value={sample.comment}
                                    onChange={v => setSample(p => ({ ...p, comment: v }))}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onValidate(sample)} variant='contained' data-cy='validate_sdage'>
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

SampleDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    selectedSample: PropTypes.shape({}),
}

const SampleTable = () => {
    const dispatch = useDispatch()
    const {
        hydrobioOperation,
        operationSamples,
        station,
    } = useShallowEqualSelector(store => ({
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
        operationSamples: store.OperationReducer.operationSamples,
        station: store.QualityReducer.qualitometer,
    }), shallowEqual)

    const indexedSandreFacies = useSandreListIndexed('PRELEVEMENTS.FACIES')
    const indexedSandreDiatoms = useSandreListIndexed('PRELEVEMENTS.DIATOMEES')
    const indexedSandreSealingType = useSandreListIndexed('PRELEVEMENTS.TYPECOLMATAGE')
    const indexedSandreHeight = useSandreListIndexed('PRELEVEMENTS.HAUTEUR')
    const indexedSandreMaterial = useSandreListIndexed('PRELEVEMENTS.MATERIELS', 'mnemonique')
    const indexedSandreShade = useSandreListIndexed('PRELEVEMENTS.OMBRAGE')
    const indexedSandreSealing = useSandreListIndexed('PRELEVEMENTS.COLMATAGE')
    const indexedSandreSpeed = useSandreListIndexed('PRELEVEMENTS.VITESSE')
    const indexedSandrePhase = useSandreListIndexed('PRELEVEMENTS.PHASE')
    const indexedSandreSubstrat = useSandreListIndexed('SUBSTRAT', 'mnemonique')
    const indexedSandreStability = useSandreListIndexed('PRELEVEMENTS.STABILITE')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)
    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)
    const [selectedSample, setSelectedSample] = useState()

    const sampleHeaders = useMemo(() => {
        if (hydrobioOperation.support === 10) { // Diatomées
            return ['substrate', 'height', 'material', 'speedClass', 'secondSubstrate', 'nbSupports', 'distanceShore', 'sealingType', 'sealingIntensity', 'vegetation', 'shade', 'morphologicalFacies', 'diatoms', 'comment']
        }
        // Macroinvertébrés
        return [{ key: 'sample', label: i18n.sample, sticky: ALIGN.LEFT }, 'phase', 'substrate', 'material', 'speedClass', 'height', 'secondSubstrate', 'distanceShore', 'sealingType', 'sealingIntensity', 'vegetation', 'shade', 'stability', 'comment']
    }, [hydrobioOperation])

    const formattedOperationSamples = useMemo(() => {
        const getHeightLabel = waterHeight => {
            if (isNil(waterHeight)) return undefined
            if (!isHugeWaterFlow(station)) return `${waterHeight} m`
            const height = indexedSandreHeight[waterHeight]
            return !isNil(height) ? `${height.comment} ${showSandreCode ? `[${height.name}]` : ''}` : undefined
        }
        return operationSamples.map(os => {
            const substrate = indexedSandreSubstrat[os.substrateCode]
            const secondSubstrate = indexedSandreSubstrat[os.secondSubstrateCode]
            return {
                sample: os.sampleCode,
                phase: indexedSandrePhase[os.phase]?.name,
                substrate: !isNil(substrate) ? `${substrate.name} ${showSandreCode ? `[${os.substrateCode}]` : ''}` : undefined,
                secondSubstrate: !isNil(secondSubstrate) ? `${secondSubstrate.name} ${showSandreCode ? `[${os.secondSubstrateCode}]` : ''}` : undefined,
                speedClass: indexedSandreSpeed[os.speedClass]?.name,
                height: getHeightLabel(os.waterHeight),
                distanceShore: !isNil(os.distanceShore) ? `${os.distanceShore} m` : undefined,
                sealingType: indexedSandreSealingType[os.sealingType]?.name,
                sealingIntensity: indexedSandreSealing[os.sealing]?.name,
                material: indexedSandreMaterial[os.material]?.name,
                vegetation: os.vegetation,
                shade: indexedSandreShade[os.shade]?.name,
                morphologicalFacies: indexedSandreFacies[os.secondaryFacies]?.name,
                stability: indexedSandreStability[os.stability]?.name,
                diatoms: indexedSandreDiatoms[os.diatoms]?.name,
                nbSupports: os.nbSupports,
                comment: os.comment,
                id: os.id,
            }
        })
    }, [indexedSandreDiatoms, indexedSandreFacies, indexedSandreHeight, indexedSandreMaterial, indexedSandrePhase, indexedSandreSealing, indexedSandreSealingType, indexedSandreShade, indexedSandreSpeed, indexedSandreStability, indexedSandreSubstrat, operationSamples, station, showSandreCode])

    const operationSample = operationSamples.find(os => os.id === selectedSample)

    return (
        <>
            <CardTable
                title={i18n.samplesProd}
                actions={[
                    { icon: 'note_add', onClick: onOpen },
                ]}

                rows={formattedOperationSamples}
                headers={sampleHeaders}
                rowsPerPageOptions={[{ value: 100, label: '100' }]}

                lineActions={[
                    {
                        icon: 'delete',
                        onClick: ({ id }) => {
                            setSelectedSample(id)
                            onOpenConfirm()
                        },
                    },
                    {
                        icon: 'edit',
                        onClick: ({ id }) => {
                            setSelectedSample(id)
                            onOpen()
                        },
                    },
                ]}
            />
            <SampleDialog
                isOpen={isOpen}
                onClose={() => {
                    setSelectedSample()
                    onClose()
                }}
                onValidate={sample => {
                    const {
                        qualitometer,
                        id,
                    } = hydrobioOperation
                    const newSample = { qualitometerId: qualitometer, operationId: id, ...sample }
                    const promise = isNil(operationSample) ? HydrobioAction.createOperationSample : HydrobioAction.updateOperationSample
                    dispatch(promise(qualitometer, id, newSample)).then(() => {
                        dispatch(HydrobioAction.fetchOperationSamples(qualitometer, id)).then(() => {
                            setSelectedSample()
                            onClose()
                        })
                    })
                }}
                selectedSample={operationSample}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title={i18n.deletingConfirmation}
                onValidate={() => {
                    const { qualitometerId, operationId, id } = operationSample
                    dispatch(HydrobioAction.deleteOperationSample(qualitometerId, operationId, id)).then(() => {
                        dispatch(HydrobioAction.fetchOperationSamples(qualitometerId, operationId)).then(() => {
                            setSelectedSample()
                            onCloseConfirm()
                        })
                    })
                }}
                onClose={onCloseConfirm}
            />
        </>
    )
}

const COVERS = [
    { substrateCode: 'S1', code: 'A' },
    { substrateCode: 'S2', code: 'B' },
    { substrateCode: 'S3', code: 'C' },
    { substrateCode: 'S28', code: 'D' },
    { substrateCode: 'S24', code: 'E' },
    { substrateCode: 'S30', code: 'F' },
    { substrateCode: 'S9', code: 'G' },
    { substrateCode: 'S10', code: 'H' },
    { substrateCode: 'S11', code: 'I' },
    { substrateCode: 'S25', code: 'J' },
    { substrateCode: 'S18', code: 'K' },
    { substrateCode: 'S29', code: 'L' },
    { substrateCode: 'S31', code: 'M' },
]

const CoverDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    selectedCover = {},
}) => {
    const indexedSandreSubstrat = useSandreListIndexed('SUBSTRAT', 'mnemonique')
    const sandreCoverType = useSandreList('RECOUVREMENT.TYPE')

    const [typeCover, setTypeCover] = useState()
    const [cover, setCover] = useState(0)
    const [shoreCover, setShoreCover] = useState(0)
    const [intermediateCover, setIntermediateCover] = useState(0)
    const [channelCover, setChannelCover] = useState(0)

    useEffect(() => {
        if (!isOpen) return
        setTypeCover(selectedCover.typeCover)
        setCover(selectedCover.cover)
        setShoreCover(selectedCover.shoreCover)
        setIntermediateCover(selectedCover.intermediateCover)
        setChannelCover(selectedCover.channelCover)
    }, [isOpen, selectedCover])

    const formattedSubstrate = COVERS.map(c => ({
        id: c.substrateCode,
        label: `${indexedSandreSubstrat[c.substrateCode]?.name ?? i18n.unknow} [${c.substrateCode}]`,
    }))

    return (
        <Dialog
            maxWidth='md'
            onClose={onClose}
            open={isOpen}
        >
            <DefaultDialogTitle
                title={i18n.covering}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container spacing={1} alignItems='center'>
                            <Grid size={12}>
                                <Select
                                    options={formattedSubstrate}
                                    label={i18n.substrate}
                                    value={selectedCover.substrateCode}
                                    disabled
                                />
                            </Grid>
                            <Grid size={6}>
                                <Select
                                    options={sandreCoverType}
                                    label={i18n.type}
                                    value={typeCover}
                                    onChange={setTypeCover}
                                />
                            </Grid>
                            <Grid size={6}>
                                <NumberField
                                    title={`${i18n.covering} (%)`}
                                    value={cover}
                                    onChange={setCover}
                                    floatValue
                                />
                            </Grid>
                            <Grid size={4}>
                                <NumberField
                                    title={i18n.shoreCover}
                                    value={shoreCover}
                                    onChange={setShoreCover}
                                    floatValue
                                />
                            </Grid>
                            <Grid size={4}>
                                <NumberField
                                    title={i18n.intermediateCover}
                                    value={intermediateCover}
                                    onChange={setIntermediateCover}
                                    floatValue
                                />
                            </Grid>
                            <Grid size={4}>
                                <NumberField
                                    title={i18n.channelCover}
                                    value={channelCover}
                                    onChange={setChannelCover}
                                    floatValue
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => onValidate({
                        typeCover: isNil(typeCover) ? undefined : `${typeCover}`,
                        cover,
                        channelCover,
                        shoreCover,
                        intermediateCover,
                        substrateCode: selectedCover.substrateCode,
                    })}
                    variant='contained'
                    data-cy='validate_sdage'
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

CoverDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    selectedCover: PropTypes.shape({}),
}

const CoverTable = () => {
    const dispatch = useDispatch()
    const {
        hydrobioOperation,
        operationCovers,
    } = useShallowEqualSelector(store => ({
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
        operationCovers: store.OperationReducer.operationCovers,
    }), shallowEqual)

    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)

    const indexedSandreSubstrat = useSandreListIndexed('SUBSTRAT', 'mnemonique')
    const indexedSandreCoverType = useSandreListIndexed('RECOUVREMENT.TYPE')

    const [selectedSubstrate, setSelectedSubstrate] = useState()

    const formattedOperationCovers = useMemo(() => {
        const formattedCovers = COVERS.map(({ substrateCode, code }) => {
            const substrate = indexedSandreSubstrat[substrateCode]
            const oc = operationCovers.find(c => c.substrateCode === substrateCode)
            const typeCover = indexedSandreCoverType[oc?.typeCover]
            return {
                substrate: `${code} - ${substrate?.name ?? i18n.unknown} [${substrateCode}]`,
                type: !isNil(oc?.typeCover) ? `${typeCover?.name ?? i18n.unknown} [${oc?.typeCover}]` : undefined,
                cover: oc?.cover,
                shoreCover: oc?.shoreCover,
                intermediateCover: oc?.intermediateCover,
                channelCover: oc?.channelCover,
                substrateCode,
            }
        })
        const percet = sumBy(formattedCovers, 'cover')
        const percetShore = sumBy(formattedCovers, 'shoreCover')
        const percetInt = sumBy(formattedCovers, 'intermediateCover')
        const percetChannel = sumBy(formattedCovers, 'channelCover')
        const sumCover = {
            substrate: i18n.sum,
            cover: {
                value: percet,
                color: percet ? percet > 100 && PASTEL_COLOR.red || percet !== 100 && PASTEL_COLOR.orange || PASTEL_COLOR.green : undefined,
            },
            shoreCover: {
                value: percetShore,
                color: percetShore ? percetShore > 100 && PASTEL_COLOR.red || percetShore !== 100 && PASTEL_COLOR.orange || PASTEL_COLOR.green : undefined,
            },
            intermediateCover: {
                value: percetInt,
                color: percetInt ? percetInt > 100 && PASTEL_COLOR.red || percetInt !== 100 && PASTEL_COLOR.orange || PASTEL_COLOR.green : undefined,
            },
            channelCover: {
                value: percetChannel,
                color: percetChannel ? percetChannel > 100 && PASTEL_COLOR.red || percetChannel !== 100 && PASTEL_COLOR.orange || PASTEL_COLOR.green : undefined,
            },
            lineAction: false,
        }
        return [
            ...formattedCovers,
            sumCover,
        ]
    }, [indexedSandreCoverType, indexedSandreSubstrat, operationCovers])

    const operationCover = operationCovers.find(oc => oc.substrateCode === selectedSubstrate)

    return (
        <>
            <CardTable
                title={i18n.covering}

                rows={formattedOperationCovers}
                headers={['substrate', 'type', { key: 'cover', value: `${i18n.covering} (%)` }, 'shoreCover', 'intermediateCover', 'channelCover']}
                rowsPerPageOptions={[{ value: 100, label: '100' }]}

                lineActions={[
                    {
                        icon: 'edit',
                        onClick: ({ substrateCode }) => {
                            setSelectedSubstrate(substrateCode)
                            onOpen()
                        },
                    },
                ]}
            />
            <CoverDialog
                isOpen={isOpen}
                onClose={onClose}
                onValidate={cover => {
                    const {
                        qualitometer,
                        id,
                    } = hydrobioOperation
                    const newCover = { qualitometerId: qualitometer, operationId: id, id: operationCover?.id, ...cover }
                    const promise = isNil(operationCover) ? HydrobioAction.createOperationCover : HydrobioAction.updateOperationCover
                    dispatch(promise(qualitometer, id, newCover)).then(() => {
                        dispatch(HydrobioAction.fetchOperationCovers(qualitometer, id))
                        onClose()
                    })
                }}
                selectedCover={operationCover ?? { substrateCode: selectedSubstrate }}
            />
        </>
    )
}

const HydrobioSample = () => {
    const dispatch = useDispatch()
    const { id: qualitometerId, idOperation: operationId } = useParams()
    const {
        hydrobioOperation,
    } = useShallowEqualSelector(store => ({
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
    }), shallowEqual)

    useEffect(() => {
        if (!qualitometerId || !operationId) return
        dispatch(HydrobioAction.fetchOperationSamples(qualitometerId, operationId))
        dispatch(HydrobioAction.fetchOperationCovers(qualitometerId, operationId))
    }, [dispatch])

    return (
        <Card className='no-box-shadow' cardStyle={{ padding: '10' }}>
            <Grid container spacing={1}>
                <Grid size={12}>
                    <SampleTable />
                </Grid>
                {hydrobioOperation.support === 13 && (
                    <Grid size={12}>
                        <CoverTable />
                    </Grid>
                )}
            </Grid>
        </Card>
    )
}

export default HydrobioSample